import { ICv } from 'types/cvsInterfaces'
import { IPerson } from 'types/userInterfaces'

/** Wizard step slugs in the order the steps are shown. Used as the `:step` route segment. */
export const WIZARD_STEPS = [
  'personal',
  'description',
  'employers',
  'projects',
  'skills',
  'education',
  'languages',
  'leisure',
] as const

export type WizardStep = (typeof WIZARD_STEPS)[number]

/**
 * Checks if the given value is a known wizard step slug.
 *
 * @param value - Value to check, e.g. the `:step` route param.
 * @returns true if the value is a wizard step.
 */
export const isWizardStep = (value: string | undefined): value is WizardStep =>
  !!value && (WIZARD_STEPS as readonly string[]).includes(value)

/**
 * Returns the translation key of a step's label.
 *
 * @param step - Wizard step.
 * @returns Translation key.
 */
export const getStepLabelKey = (step: WizardStep) => `profile.wizard.step.${step}`

/**
 * Builds the route path of a wizard step.
 *
 * @param personId - ID of the profile's person.
 * @param cvId - ID of the CV being filled.
 * @param step - Wizard step.
 * @returns Route path.
 */
export const getWizardPath = (personId: number, cvId: number, step: WizardStep = WIZARD_STEPS[0]) =>
  `/profile/${personId}/wizard/${cvId}/${step}`

/** CV sections the wizard can show. */
export type WizardSection =
  | 'about'
  | 'description'
  | 'employers'
  | 'projects'
  | 'skills'
  | 'education'
  | 'courses'
  | 'certificates'
  | 'competences'
  | 'languages'
  | 'references'
  | 'leisureActivities'
  | 'learningGoals'

/** Sections of each step, in display order. */
export const WIZARD_STEP_SECTIONS: Record<WizardStep, WizardSection[]> = {
  personal: ['about'],
  description: ['description'],
  employers: ['employers'],
  projects: ['projects'],
  skills: ['skills'],
  education: ['education', 'courses', 'certificates', 'competences'],
  languages: ['languages', 'references'],
  leisure: ['leisureActivities', 'learningGoals'],
}

/** What section visibility depends on. */
export interface IWizardSectionContext {
  /** Features of the logged in user, from `useUser()`. */
  features: string[]
  /** User is in the sales group of the person's organization. */
  salesAccess: boolean
  /** User is in the company admin group of the person's organization. */
  companyAdminAccess: boolean
}

/** Feature gates. Same rules as the profile page; sections not listed are always shown. */
const SECTION_GATES: Partial<Record<WizardSection, (context: IWizardSectionContext) => boolean>> = {
  competences: ({ features }) => features.includes('competences'),
  learningGoals: ({ features, salesAccess, companyAdminAccess }) =>
    features.includes('learningGoals') && (salesAccess || companyAdminAccess),
}

/**
 * Checks if a section is shown to the user.
 *
 * @param section - CV section.
 * @param context - Features and access of the user.
 * @returns true if the section is shown.
 */
export const isSectionVisible = (section: WizardSection, context: IWizardSectionContext) =>
  SECTION_GATES[section]?.(context) ?? true

/**
 * Returns the sections of a step that are shown to the user.
 *
 * @param step - Wizard step.
 * @param context - Features and access of the user.
 * @returns Visible sections in display order.
 */
export const getVisibleSections = (step: WizardStep, context: IWizardSectionContext) =>
  WIZARD_STEP_SECTIONS[step].filter(section => isSectionVisible(section, context))

/**
 * Returns the steps that have at least one visible section.
 *
 * @param context - Features and access of the user.
 * @returns Visible steps in order.
 */
export const getVisibleSteps = (context: IWizardSectionContext): WizardStep[] =>
  WIZARD_STEPS.filter(step => getVisibleSections(step, context).length > 0)

/**
 * Handler a step registers to run when Next/Finish is clicked, e.g. to save the step.
 * Resolves to false to stay on the step (e.g. invalid input). A thrown error is shown and keeps the step open.
 */
export type WizardBeforeNext = () => Promise<boolean>

/** Props every wizard step component gets. */
export interface IWizardStepProps {
  /** ID of the profile's person. */
  personId: number
  /** ID of the CV being filled. */
  cvId: number
  /** Sections of the step visible to the user. */
  sections: WizardSection[]
  /** Registers (or clears with undefined) the step's Next handler. */
  registerBeforeNext: (handler: WizardBeforeNext | undefined) => void
}

/** Saved data the stepper's completed marks are based on. */
export interface IWizardCompletionData {
  /** The CV with its sections (`cvAPI.getCvData`). */
  cv: ICv
  /** The person, for the personal details. */
  person?: Pick<IPerson, 'firstName' | 'lastName'>
  /** Learning goals of the person; only loaded when that section is visible. */
  learningGoalCount: number
  /** UI language; the description is checked in it. */
  language: string
}

/** Whether a section has saved data. */
const SECTION_HAS_DATA: Record<WizardSection, (data: IWizardCompletionData) => boolean> = {
  about: ({ person }) => !!person?.firstName?.trim() && !!person?.lastName?.trim(),
  description: ({ cv, language }) =>
    !!cv.translations?.find(translation => translation.Language?.name === language)?.about?.trim(),
  employers: ({ cv }) => (cv.Employers?.length ?? 0) > 0,
  projects: ({ cv }) => (cv.Projects?.length ?? 0) > 0,
  skills: ({ cv }) => (cv.PersonSkills?.length ?? 0) + (cv.Industries?.length ?? 0) + (cv.Roles?.length ?? 0) > 0,
  education: ({ cv }) => (cv.Education?.length ?? 0) > 0,
  courses: ({ cv }) => (cv.Courses?.length ?? 0) > 0,
  certificates: ({ cv }) => (cv.PersonCertificates?.length ?? 0) > 0,
  competences: ({ cv }) => (cv.PersonCompetences?.length ?? 0) > 0,
  languages: ({ cv }) => (cv.LanguageSkills?.length ?? 0) > 0,
  references: ({ cv }) => (cv.References?.length ?? 0) > 0,
  leisureActivities: ({ cv }) => (cv.LeisureActivities?.length ?? 0) > 0,
  learningGoals: ({ learningGoalCount }) => learningGoalCount > 0,
}

/**
 * Returns the steps to mark as completed: a step is completed when one of its visible sections has saved data.
 * Sections hidden by feature gates don't count.
 *
 * @param steps - Visible steps.
 * @param context - Features and access of the user.
 * @param data - Saved data.
 * @returns Completed steps.
 */
export const getCompletedSteps = (
  steps: WizardStep[],
  context: IWizardSectionContext,
  data: IWizardCompletionData
): WizardStep[] =>
  steps.filter(step => getVisibleSections(step, context).some(section => SECTION_HAS_DATA[section](data)))
